import { Button } from "@clockwise/design-system";
import { Close, Link } from "@clockwise/design-system/icons";
import { CopyText } from "@clockwise/web-commons/src/ui/copy-text/CopyText";
import { narrowMediaQuery } from "@clockwise/web-commons/src/ui/scheduling-link/scheduling-link.styles";
import { TrackingEvents, useTrackOnce } from "@clockwise/web-commons/src/util/analytics.util";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { CircularProgress, Dialog, Paper, PaperProps, makeStyles } from "@material-ui/core";
import { animated, config, useSpring } from "@react-spring/web";
import { DateTime } from "luxon";
import React, { useCallback, useState } from "react";
import { Chip } from "../../../../chip";
import { ShareBestTimesLinkModal } from "../../../../scheduling-link";
import { SidebarLink } from "../../../../scheduling-link/LinksSection";
import { formattedDateTime } from "../../../../web-app-calendar/calendar-popover/utils/formattedDateTime";
import {
  GQLAddDiffSummary,
  GQLConsequencesBlock,
  GQLModifyDiffSummary,
  GQLProfile,
} from "../../utils/types";
import { ProposalCopyTimesPreview } from "./ProposalCopyTimesPreview";

const useStyles = makeStyles({
  paper: {
    padding: "26px 34px 30px",
    minWidth: 320,
    maxWidth: 550,
    boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.25)",
    borderRadius: 8,

    [narrowMediaQuery]: {
      height: "100%",
    },
  },
});

interface IProps {
  canShareProposal: boolean;
  viewerProfile: GQLProfile | null;
  isProposeNewTime: boolean;
  sharedProposalLink: string | null;
  sharedProposalDiff: GQLAddDiffSummary | GQLModifyDiffSummary | null;
  sharedProposalConsequencesBlock: GQLConsequencesBlock | null;
  schedulingLink: SidebarLink | null;
  reschedulingLinkUrl: string | null;
  schedulingLinkLoading: boolean;
  selectedTime: DateTime | null;
  fullscreen?: boolean;
  onShareSchedulingLink: () => void;
  onCopy: (text: string) => void;
  onClose: () => void;
}

export const ShareProposalModal = ({
  canShareProposal,
  viewerProfile,
  isProposeNewTime,
  sharedProposalLink,
  sharedProposalConsequencesBlock,
  sharedProposalDiff,
  schedulingLink,
  reschedulingLinkUrl,
  schedulingLinkLoading,
  selectedTime,
  fullscreen = false,
  onShareSchedulingLink,
  onCopy,
  onClose,
}: IProps) => {
  const trackCopySharedProposalLink = useTrackOnce(
    TrackingEvents.SHAREABLE_PROPOSAL.MODAL.COPY_LINK,
  );
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(true);
  const [sharedProposalViewActive, setSharedProposalViewActive] = useState(canShareProposal);
  // TODO: Use timezone value from BE
  const targetTimeZone = getRenderTimeZone();

  const modalStyles = useSpring({
    from: {
      scale: 0.83,
      opacity: 0,
    },
    to: {
      scale: 1,
      opacity: 1,
    },
    config: config.stiff,
  });

  const AnimatedPaper = animated(Paper);

  const handleCopyLink = useCallback(
    (link: string) => {
      onCopy(link);
      trackCopySharedProposalLink();
    },
    [trackCopySharedProposalLink, onCopy],
  );

  const handleOnShareSchedulingLink = () => {
    if (!schedulingLink) {
      void onShareSchedulingLink();
    }
    setSharedProposalViewActive(false);
  };

  const renderPills = () => {
    return (
      <>
        <Chip
          sentiment={sharedProposalViewActive ? "emphasis" : "neutral"}
          onClick={() => setSharedProposalViewActive(true)}
          key={"share-proposal"}
          variant="solid"
        >
          Share this proposal
          {selectedTime && `  (${formattedDateTime(selectedTime, getRenderTimeZone())})`}
        </Chip>
        {!isProposeNewTime && (
          <Chip
            sentiment={sharedProposalViewActive ? "neutral" : "emphasis"}
            onClick={() => handleOnShareSchedulingLink()}
            key={"share-scheduling-link"}
            variant="solid"
          >
            Share multiple times
          </Chip>
        )}
      </>
    );
  };

  const showSharedProposal = sharedProposalViewActive && sharedProposalLink;
  const showScheduleLink = !sharedProposalViewActive && (schedulingLink || reschedulingLinkUrl);

  return (
    <Dialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
        onClose();
      }}
      fullScreen={fullscreen}
      disableRestoreFocus
      disableScrollLock
      PaperComponent={AnimatedPaper as React.ComponentType<PaperProps>}
      PaperProps={{
        style: {
          // PaperProps wants CSS properties so must cast the animated values to number
          scale: (modalStyles.scale as unknown) as number,
          opacity: (modalStyles.opacity as unknown) as number,
          visibility: dialogOpen ? "visible" : "hidden",
        },
        className: classes.paper,
      }}
      cw-id="share-proposal-modal"
    >
      <div className="cw-flex cw-justify-between cw-items-center cw-mb-3">
        <h2 className="cw-heading-xl">Share this proposal</h2>
        <div className="-cw-mr-[10px]">
          <Button
            variant="text"
            size="large"
            startIcon={Close}
            onClick={onClose}
            cw-id="close-copy-best-times-modal"
          />
        </div>
      </div>

      {canShareProposal && (
        <div className="cw-flex cw-gap-3 cw-items-center cw-mb-3 cw-text-xl">{renderPills()}</div>
      )}

      {showSharedProposal && (
        <div className="cw-flex cw-flex-col cw-gap-3 cw-body-base">
          <div>
            This link will let the recipient view this proposal
            {selectedTime && " with your selected time of "}
            {selectedTime && (
              <span className="cw-font-semibold">
                {formattedDateTime(selectedTime, getRenderTimeZone(), true)}
              </span>
            )}
            , and book the meeting
          </div>
          <CopyText
            copyAriaLabel={"Copy link to proposal"}
            onCopy={() => handleCopyLink(`${sharedProposalLink}?cw_source=copy_link`)}
            startIcon={<Link />}
            text={sharedProposalLink}
            urlFormatting
          />

          {sharedProposalDiff && (
            <>
              <div className="cw-mt-2">Or, copy this text to give recipients more information:</div>
              <ProposalCopyTimesPreview
                title={sharedProposalDiff.title}
                viewerGivenName={viewerProfile?.givenName || null}
                selectedTime={selectedTime}
                isReschedule={sharedProposalDiff.__typename === "ModifyDiffSummary"}
                isProposeNewTime={isProposeNewTime}
                consequencesBlock={sharedProposalConsequencesBlock}
                sharedProposalLink={`${sharedProposalLink}?cw_source=copy_times`}
                targetTimeZone={targetTimeZone}
                onCopyRichText={onCopy}
              />
            </>
          )}
        </div>
      )}
      {!sharedProposalViewActive && schedulingLinkLoading && (
        <div className="cw-flex cw-justify-center cw-mt-3 cw-gap-3 cw-body-base">
          <CircularProgress />
        </div>
      )}
      {showScheduleLink && schedulingLink && schedulingLink.linkName && !schedulingLinkLoading && (
        <ShareBestTimesLinkModal
          url={schedulingLink.url}
          rescheduleUrl={reschedulingLinkUrl}
          linkName={schedulingLink.linkName}
          name={schedulingLink.schedulingLink.name}
          slug={schedulingLink.schedulingLink.slug}
          durations={schedulingLink.schedulingLink.durations}
          defaultDuration={schedulingLink.schedulingLink.defaultDuration}
          windowSizeDays={
            schedulingLink.schedulingLink.availabilityRange?.__typename === "RollingWindow"
              ? schedulingLink.schedulingLink.availabilityRange.windowSize
              : null
          }
          multipleUser={schedulingLink.schedulingLink.linkMembers.length > 1}
          onClose={() => {}}
          fullscreen={false}
          renderedFrom={"shareable-proposal"}
          renderContentInModal={false}
        />
      )}
    </Dialog>
  );
};
